import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import CountUp from "react-countup";
import { API_BASE } from "../api/config";
import { useAuth } from "../context/AuthContext";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import { useNavigate } from "react-router-dom"; // ⬅️ added

function Dashboard() {
  const { username, branch, loginType } = useAuth();

  const navigate = useNavigate(); // ⬅️ added for navigation

  // 🗓️ Date states
  const today = new Date().toISOString().split("T")[0];
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);

  // 📊 Dashboard data state
  const [dashboardData, setDashboardData] = useState({
    total_sales: 0,
    total_payment_credit: 0,
    total_payment_debit: 0,
    profit_and_loss: 0,
  });

  const [stackedData, setStackedData] = useState([]);
  const [storeDetails, setStoreDetails] = useState([]);
  const [tableDetails, setTableDetails] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchTableTerm, setSearchTableTerm] = useState("");
  
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(branch || "");
  
  useEffect(() => {
  console.log("Dashboard loginType:", loginType, typeof loginType);
}, [loginType]);

  
  const fetchBranches = async () => {
  try {
    const res = await fetch(
      `${API_BASE}?key=dzital_key&pswd=Hibujji@123&transac=get&table=branches`
    );
    const data = await res.json();

    if (Array.isArray(data)) {
      setBranches(data);
    } else {
      setBranches([]);
    }
  } catch (err) {
    console.error("Error fetching branches:", err);
    setBranches([]);
  }
};


useEffect(() => {
  fetchBranches();
}, []);

useEffect(() => {
  if (selectedBranch) {
    fetchDashboardData();
  }
}, [fromDate, toDate, selectedBranch]);



  // 📡 Fetch dashboard totals
  const fetchDashboardData = async (from = fromDate, to = toDate) => {
    try {
      const url = `${API_BASE}?key=dzital_key&pswd=Hibujji@123&transac=get_totals_to_dashboard&from_date=${from}&to_date=${to}&branch=${encodeURIComponent(
        selectedBranch
      )}`;
      const res = await fetch(url);
      const data = await res.json();

      if (data.status === "success") {
        setStackedData([
          {
            month: data.before_previous_month.month,
            Sales: Number(data.before_previous_month.total_sales || 0),
          },
          {
            month: data.previous_month.month,
            Sales: Number(data.previous_month.total_sales || 0),
          },
          {
            month: data.current_month.month,
            Sales: Number(data.current_month.total_sales || 0),
          },
        ]);

        // ✅ Safely set store details (never null)
        setStoreDetails(data.arr_store_details || []);
        setTableDetails(data.arr_table_details || []);

        setDashboardData({
          total_sales: Number(data.total_sales || 0),
          total_payment_credit: Number(data.total_payment_credit || 0),
          total_payment_debit: Number(data.total_payment_debit || 0),
          profit_and_loss: Number(data.profit_and_loss || 0),
        });
      } else {
        setDashboardData({
          total_sales: 0,
          total_payment_credit: 0,
          total_payment_debit: 0,
          profit_and_loss: 0,
        });
        setStoreDetails([]);
        setTableDetails([]);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setStoreDetails([]); // 👈 also reset safely
      setTableDetails([]); // 👈 also reset safely
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [fromDate, toDate]);

  // 🔍 Filtered search data (safe guard for null)
  const filteredDetails = (storeDetails || []).filter((item) =>
    item.item_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // 🔍 Filtered search table data (safe guard for null)
  const filteredTableDetails = (tableDetails || []).filter((table) =>
    table.table_name?.toLowerCase().includes(searchTableTerm.toLowerCase())
  );

  return (
    <Layout>
      <div className="p-6">
        {/* 🗓️ Date Filters */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8 bg-white shadow-sm rounded-xl p-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">
              Dashboard Overview by Date Range
            </h2>
            <p className="text-sm text-gray-500">
              Select a custom range to update the dashboard
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-4">
		  
		   {/* Branch Dropdown */}
    <select
      value={selectedBranch}
      onChange={(e) => setSelectedBranch(e.target.value)}
      className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 shadow-sm text-gray-800 focus:ring-2 focus:ring-blue-500"
    >
      <option value="0">All Restaurants</option>
      {branches.map((b, index) => (
        <option key={index} value={b.name}>
          {b.name}
        </option>
      ))}
    </select>
		  
            <div className="flex items-center bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 shadow-sm focus-within:ring-2 focus-within:ring-blue-500">
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="bg-transparent text-gray-800 focus:outline-none"
              />
            </div>

            <div className="flex items-center bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 shadow-sm focus-within:ring-2 focus-within:ring-blue-500">
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="bg-transparent text-gray-800 focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* 📊 Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {[
            { label: "Sales", value: dashboardData.total_sales, color: "text-green-600", route: "/sales" },
            { label: "Receipts", value: dashboardData.total_payment_credit, color: "text-orange-500", route: "/payments" },
            { label: "Payments", value: dashboardData.total_payment_debit, color: "text-red-600", route: "/payments" },
            { label: "Profit / Loss", value: dashboardData.profit_and_loss, color: "text-purple-600" },
          ].map((card) => (
            <div
              key={card.label}
              onClick={() => {
                if (card.route) navigate(card.route);
              }}
              className={`p-6 bg-white shadow rounded-2xl ${
                card.route ? "cursor-pointer hover:shadow-lg transition-shadow duration-200" : ""
              }`}
            >
              <p className="text-gray-500">{card.label}</p>
              <h2 className={`text-2xl font-bold ${card.color}`}>
                ₹
                <CountUp
                  end={card.value}
                  duration={1.5}
                  separator=","
                  decimals={2}
                />
              </h2>
            </div>
          ))}
        </div>

        {/* 📈 Chart & 🧾 Store Details */}
       <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">

         

          {/* Store Details Table */}
        <div className="bg-white p-6 shadow rounded-2xl">
  <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-4">
    <h3 className="text-lg font-semibold text-gray-800">Sales Details</h3>

    <input
      type="text"
      placeholder="Search by item name..."
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      className="border border-gray-300 rounded-lg px-3 py-2 w-full md:w-64 text-black focus:ring-2 focus:ring-blue-500 focus:outline-none"
    />
  </div>

  {/* Fixed height scrollable table */}
  <div className="rounded-lg border border-gray-200">
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-100 text-gray-700 sticky top-0 z-10">
          <tr>
            <th className="px-4 py-2 text-left w-12">#</th>
            <th className="px-4 py-2 text-left">Item Name</th>
            <th className="px-4 py-2 text-right">Quantity</th>
            <th className="px-4 py-2 text-right">Subtotal (₹)</th>
          </tr>
        </thead>
      </table>
    </div>

    {/* Scrollable body */}
    <div className="overflow-y-auto" style={{ maxHeight: "280px" }}>
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <tbody>
          {filteredDetails.length > 0 ? (
            filteredDetails.map((item, index) => (
              <tr key={index} className="hover:bg-gray-50 text-gray-700">
                <td className="px-4 py-2">{index + 1}</td>
                <td className="px-4 py-2">{item.item_name}</td>
                <td className="px-4 py-2 text-right">
                  {Number(item.total_qty || item.item_qty || 0).toLocaleString()}
                </td>
                <td className="px-4 py-2 text-right">
                  ₹{Number(item.total_subtotal || item.subtotal || 0).toFixed(2)}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={4} className="px-4 py-4 text-center text-gray-500">
                No records found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>

    {/* Footer fixed */}
    {filteredDetails.length > 0 && (
      <div className="bg-gray-50 border-t border-gray-200">
        <table className="min-w-full text-sm font-semibold text-gray-800">
          <tfoot>
            <tr>
              <td className="px-4 py-2 text-left" colSpan={2}>
                Total
              </td>
              <td className="px-4 py-2 text-right">
                {filteredDetails
                  .reduce((s, item) => s + Number(item.total_qty || item.item_qty || 0), 0)
                  .toLocaleString()}
              </td>
              <td className="px-4 py-2 text-right">
                ₹{filteredDetails
                  .reduce((s, item) => s + Number(item.total_subtotal || item.subtotal || 0), 0)
                  .toFixed(2)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    )}
  </div>
</div>


          {/* Table Details Table */}
         <div className="bg-white p-6 shadow rounded-2xl">
  <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-4">
    <h3 className="text-lg font-semibold text-gray-800">Table Details</h3>

    <input
      type="text"
      placeholder="Search by table name..."
      value={searchTableTerm}
      onChange={(e) => setSearchTableTerm(e.target.value)}
      className="border border-gray-300 rounded-lg px-3 py-2 w-full md:w-64 text-black focus:ring-2 focus:ring-blue-500 focus:outline-none"
    />
  </div>

            
  <div className="rounded-lg border border-gray-200">
    <div className="overflow-x-auto">
                 <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-100 text-gray-700 sticky top-0 z-10">
          <tr>
            <th className="px-4 py-2 text-left w-12">#</th>
            <th className="px-4 py-2 text-left">Table Name</th>
            <th className="px-4 py-2 text-right">Subtotal (₹)</th>
          </tr>
        </thead>
      </table>
    </div>

                {/* Scroll body */}
    <div className="overflow-y-auto" style={{ maxHeight: "250px" }}>
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <tbody>
          {filteredTableDetails.length > 0 ? (
            filteredTableDetails.map((table, index) => (
              <tr key={index} className="hover:bg-gray-50 text-gray-700">
                <td className="px-4 py-2">{index + 1}</td>
                <td className="px-4 py-2">{table.table_name}</td>
                <td className="px-4 py-2 text-right">
                  ₹{Number(table.invoice_value || 0).toFixed(2)}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={3} className="px-4 py-4 text-center text-gray-500">
                No records found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>

  {/* Footer */}
    {filteredTableDetails.length > 0 && (
      <div className="bg-gray-50 border-t border-gray-200">
        <table className="min-w-full text-sm font-semibold text-gray-800">
          <tfoot>
            <tr>
              <td className="px-4 py-2" colSpan={2}>
                Total
              </td>
              <td className="px-4 py-2 text-right">
                ₹
                {filteredTableDetails
                  .reduce((s, t) => s + Number(t.invoice_value || 0), 0)
                  .toFixed(2)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    )}
            </div>
          </div>
		  
		   {/* Chart */}
          <div className="p-6 bg-white shadow rounded-2xl">
            <h3 className="text-lg font-semibold mb-4">Monthly Sales Overview</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stackedData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="Sales" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </div>
		  
        </div>
      </div>
    </Layout>
  );
}

export default Dashboard;
